/*
 * Plain-language copy of the Run build sheet (pure, no React). The API words its launch-form guardrails and plan checks
 * for engineers (the runner line names the provider and its CLI); the sheet says what will happen in the user's words
 * ("Hangar takes commit 6de0928 from malible/app and builds the Android app"), never naming the build service (SPEC
 * decision 23). Anything without a plain rewrite keeps the API's message (reworded for clients by the sheet), never its code.
 */
import type { Platform } from '@hangar/shared';
import type { CheckResult, Guardrail } from '@/api/schemas';
import { humanize, shortSha } from '@/lib/format';

const PLATFORM_NAME: Record<string, string> = { android: 'Android', ios: 'iOS' };

/** What the user picked: the commit (may be empty = branch head) and the branch. */
export interface CopyContext {
  commitSha?: string | null;
  branch?: string | null;
}

/** "commit 6de0928", else "the latest commit of master", else "the latest commit". */
function commitPhrase({ commitSha, branch }: CopyContext): string {
  if (commitSha) return `commit ${shortSha(commitSha)}`;
  return branch ? `the latest commit of ${branch}` : 'the latest commit';
}

/** "Hangar takes commit 6de0928 from malible/app and builds the Android app". */
export function runnerPlanText(platform: Platform | string, repository: string, ctx: CopyContext): string {
  const app = PLATFORM_NAME[platform] ? `the ${PLATFORM_NAME[platform]} app` : 'the app';
  return `Hangar takes ${commitPhrase(ctx)} from ${repository} and builds ${app}`;
}

const RUNNER_MESSAGE = /^(android|ios): Hangar's runner fetches (\S+) at the commit/;

/** A launch-form guardrail's message in plain words (the runner line rewritten; the rest as the API wrote it). */
export function guardrailText(g: Pick<Guardrail, 'code' | 'message'>, ctx: CopyContext): string {
  const runner = g.code === 'HGR-RUNNER' ? RUNNER_MESSAGE.exec(g.message) : null;
  return runner ? runnerPlanText(runner[1]!, runner[2]!, ctx) : g.message;
}

/** The commit the plan resolved for the branch (its branch check reports it), else the one the user typed. */
export function planCommitOf(checks: Pick<CheckResult, 'key' | 'details'>[], typed?: string | null): string | null {
  const resolved = checks.find((c) => c.key === 'branch_guardrail')?.details?.commitSha;
  return typeof resolved === 'string' && resolved ? resolved : typed || null;
}

/** A plan check's line: the runner's source in plain words, else the API's message, else the check's name humanized. */
export function planCheckText(c: Pick<CheckResult, 'key' | 'status' | 'message' | 'details'>, platform: Platform, ctx: CopyContext): string {
  const repository = c.details?.repository;
  if (c.key === 'runner_source' && c.status === 'passed' && typeof repository === 'string') return runnerPlanText(platform, repository, ctx);
  return c.message ?? humanize(c.key);
}
